import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { I18nProvider } from "./i18n";
import AppErrorBoundary from "./components/AppErrorBoundary";
import { getPayPalConfig } from "./lib/api";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import Deposit from "./pages/Deposit";
import DepositPayPalReturn from "./pages/DepositPayPalReturn";
import Transfer from "./pages/Transfer";
import Transactions from "./pages/Transactions";
import Exchange from "./pages/Exchange";
import Cards from "./pages/Cards";
import Settings from "./pages/Settings";
import Insights from "./pages/Insights";
import SpendingInsightsDashboard from "./pages/SpendingInsightsDashboard";
import Developer from "./pages/Developer";
import Team from "./pages/Team";
import Reports from "./pages/Reports";
import Request from "./pages/Request";
import Pay from "./pages/Pay";
import AuthCallback from "./pages/AuthCallback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import MerchantDashboard from "./pages/MerchantDashboard";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import Sessions from "./pages/Sessions";
import Contact from "./pages/Contact";
import PartnerDashboard from "./pages/PartnerDashboard";
import APIDocs from "./pages/APIDocs";
import DeveloperKeys from "./pages/DeveloperKeys";
import TravelDirectory from "./pages/TravelDirectory";
import HotelsDirectory from "./pages/HotelsDirectory";
import BanksDirectory from "./pages/BanksDirectory";
import SpendingChallenges from "./pages/SpendingChallenges";
import MerchantVerification from "./pages/MerchantVerification";
import ForexRates from "./pages/ForexRates";
import Affiliate from "./pages/Affiliate";
import AdminReferrals from "./pages/AdminReferrals";
import AdminWhatsApp from "./pages/AdminWhatsApp";
import AdminWhatsAppInbox from "./pages/AdminWhatsAppInbox";
import PublicProfile from "./pages/PublicProfile";
import InvoicePayment from "./pages/InvoicePayment";
import Invoices from "./pages/Invoices";
import About from "./pages/About";
import PWAInstallBanner from "./components/PWAInstallBanner";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Skip auth check if coming from OAuth callback
  if (location.state?.user) {
    return children;
  }
  
  // Non-blocking spinner: small centered icon, NOT full-screen cover
  // Uses pointer-events: none so it never blocks interaction
  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#1a1a2e]">
        <div 
          className="fixed top-4 right-4 z-50 pointer-events-none"
          style={{ pointerEvents: 'none' }}
        >
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
        {children}
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin route guard - requires user to be admin
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#1a1a2e]">
        <div className="fixed top-4 right-4 z-50 pointer-events-none">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is admin
  const adminEmails = ['admin@parachpay.com', 'support@parachpay.com', 'info@parachpay.com'];
  const isAdmin = (
    user.role === 'admin' ||
    user.is_admin === true ||
    adminEmails.includes(user.email?.toLowerCase())
  );
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Non-blocking spinner: small centered icon, NOT full-screen cover
  // Uses pointer-events: none so it never blocks interaction
  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#1a1a2e]">
        <div 
          className="fixed top-4 right-4 z-50 pointer-events-none"
          style={{ pointerEvents: 'none' }}
        >
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
        {children}
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Main App Router with OAuth callback detection
function AppRoutes() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/deposit" element={<ProtectedRoute><Deposit /></ProtectedRoute>} />
      <Route path="/deposit/paypal-return" element={<ProtectedRoute><DepositPayPalReturn /></ProtectedRoute>} />
      <Route path="/transfer" element={<ProtectedRoute><Transfer /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
      <Route path="/exchange" element={<ProtectedRoute><Exchange /></ProtectedRoute>} />
      <Route path="/cards" element={<ProtectedRoute><Cards /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
      <Route path="/spending-insights" element={<ProtectedRoute><SpendingInsightsDashboard /></ProtectedRoute>} />
      <Route path="/request" element={<ProtectedRoute><Request /></ProtectedRoute>} />
      <Route path="/developer" element={<ProtectedRoute><Developer /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Team /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><SpendingChallenges /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><Affiliate /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/sessions" element={<ProtectedRoute><Sessions /></ProtectedRoute>} />
      <Route path="/merchant" element={<ProtectedRoute><MerchantDashboard /></ProtectedRoute>} />
      <Route path="/partner" element={<ProtectedRoute><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/api-docs" element={<ProtectedRoute><APIDocs /></ProtectedRoute>} />
      <Route path="/developer-keys" element={<ProtectedRoute><DeveloperKeys /></ProtectedRoute>} />
      <Route path="/travel-directory" element={<TravelDirectory />} />
      <Route path="/hotels-directory" element={<HotelsDirectory />} />
      <Route path="/banks-directory" element={<BanksDirectory />} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/forex-rates" element={<ProtectedRoute><ForexRates /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/pay/:requestId" element={<Pay />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsConditions />} />
      <Route path="/about" element={<About />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/merchant-verification" element={<MerchantVerification />} />
      <Route path="/admin/referrals" element={<AdminRoute><AdminReferrals /></AdminRoute>} />
      <Route path="/admin/whatsapp" element={<AdminRoute><AdminWhatsApp /></AdminRoute>} />
      <Route path="/admin/whatsapp/inbox" element={<AdminRoute><AdminWhatsAppInbox /></AdminRoute>} />
      {/* Public Profile Page - /u/{tag} or /p/{tag} */}
      <Route path="/u/:tag" element={<PublicProfile />} />
      <Route path="/p/:tag" element={<PublicProfile />} />
      {/* Public Invoice Payment Page */}
      <Route path="/invoice/pay/:paymentToken" element={<InvoicePayment />} />
    </Routes>
  );
}

// PayPal wrapper that fetches config from backend before initializing SDK
function PayPalWrapper({ children }) {
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getPayPalConfig();
        if (response.data?.client_id) {
          setPaypalConfig(response.data);
        } else {
          console.error('PayPal config missing client_id');
        }
      } catch (error) {
        console.error('Failed to fetch PayPal config:', error);
      } finally {
        setConfigLoading(false);
      }
    };
    
    fetchConfig();
  }, []);
  
  // If still loading config or no config, render children without PayPal provider
  // The Deposit page will show its own loading/fallback state
  if (configLoading || !paypalConfig?.client_id) {
    return (
      <PayPalScriptProvider 
        options={{ 
          clientId: "sb", // Sandbox placeholder - won't be used
          currency: "USD",
          intent: "capture"
        }}
        deferLoading={true}
      >
        {children}
      </PayPalScriptProvider>
    );
  }
  
  // Once config is loaded, render with correct client ID
  // Key forces remount when client_id changes
  return (
    <PayPalScriptProvider 
      key={paypalConfig.client_id}
      options={{ 
        clientId: paypalConfig.client_id,
        currency: "USD",
        intent: "capture"
      }}
    >
      {children}
    </PayPalScriptProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <I18nProvider>
          <PayPalWrapper>
            <AuthProvider>
              <BrowserRouter>
                <AppErrorBoundary>
                  <div className="App min-h-screen bg-background">
                    <AppRoutes />
                    <Toaster richColors position="top-right" />
                    <PWAInstallBanner />
                  </div>
                </AppErrorBoundary>
              </BrowserRouter>
            </AuthProvider>
          </PayPalWrapper>
        </I18nProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
